import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { defineMessages, injectIntl, FormattedMessage } from 'react-intl';
import { useSelector } from 'react-redux';
import Logger from 'js-logger';
import get from 'lodash/get';
import { featureSelectors } from '@lifesize/nucleus';
import { hooks, media, VideoStream, constants, selectors } from '@lifesize/clients.sdk';
import { Box, makeStyles, Theme, Typography } from '@material-ui/core';
import { ReactComponent as WarningIcon } from '@lifesize/ux-assets/vectors/svgIcons/warning.svg';
import { colors } from '@lifesize/clients.mui-components';
import Button from 'components/Button/Button';
import DeviceMute, { muteType } from 'components/AvSettings/DeviceMute';
import BetaBadge, { badgeType } from 'components/Badge/Beta';
import { FLAG_VIRTUAL_BACKGROUND } from 'constants/FeatureFlags';
import { getDevicesByKind, getConstraints, isWebGL2Supported } from 'utils/deviceUtils';
import { vbbManager } from 'utils/vbbManager';
import { defaultResolutionLabel, defaultVideoAspect, resolutionMap } from 'utils/videoResolution';
import SettingDropdown from './SettingDropdown';

const messages = defineMessages({
  header: { id: 'components.avSettings.sectionHeader.VideoSettings', defaultMessage: 'Video Settings' },
  inputCamera: { id: 'components.avSettings.inputLabel.Camera', defaultMessage: 'Camera' },
  inputResolution: { id: 'components.avSettings.inputLabel.Resolution', defaultMessage: 'Resolution' },
  resolution360p: { id: 'components.avSettings.resolution360p', defaultMessage: 'Power Saving (360p)' },
  resolution4k: { id: 'components.avSettings.resolution4k', defaultMessage: 'Ultra-High Definition (4K)' },
  resolutionDefault: {
    id: 'components.avSettings.resolutionDefault',
    defaultMessage: 'Default Resolution ({resolution})'
  },
  resolutionFullHighDef: {
    id: 'components.avSettings.resolutionFullHighDef',
    defaultMessage: 'Full High Definition (1080p)'
  },
  resolutionHighDef: { id: 'components.avSettings.resolutionHighDef', defaultMessage: 'High Definition (720p)' }
});

interface Props {
  handleConfigChange: Function;
  intl: { formatMessage: Function };
  isInCall?: boolean;
  onToggleVbbSettings: Function;
  isJoinFlowCall?: boolean;
}

export const VideoSettings = (props: Props) => {
  const { handleConfigChange, intl, onToggleVbbSettings, isJoinFlowCall } = props;
  const classes = useStyles({});
  const mediaSettings = useSelector((state) => get(state, 'app.mediaSettings'));
  const vbbFeatureEnabled = useSelector((state) => featureSelectors.getFeatureFlag(state)(FLAG_VIRTUAL_BACKGROUND));
  const { videoDevice } = hooks.useMedia();
  const devices = hooks.useDevices();
  const localPrimaryStream = hooks.useLocalPrimaryStream();
  const videoInputDevices = useMemo(() => getDevicesByKind(devices, 'videoinput'), [devices]);
  const isInCall = hooks.useCallState()?.state !== constants.CALL_STATE_DISCONNECTED;
  const [webGL2Supported] = useState(isWebGL2Supported());

  const onSelectVideoSource = async (deviceId) => {
    await handleConfigChange('camera', deviceId);
  };

  const resolutionLabels = new Map([
    ['default', intl.formatMessage(messages.resolutionDefault, { resolution: defaultResolutionLabel })],
    ['2160p', intl.formatMessage(messages.resolution4k)],
    ['1080p', intl.formatMessage(messages.resolutionFullHighDef)],
    ['720p', intl.formatMessage(messages.resolutionHighDef)],
    ['360p', intl.formatMessage(messages.resolution360p)]
  ]);

  const resolutions = useMemo(
    () => Object.values(resolutionMap).map((r) => ({ label: resolutionLabels.get(r.value) || '', value: r.value })),
    [resolutionLabels]
  );

  const getDeviceValue = useCallback(() => {
    const selectedDevice =
      videoInputDevices.find((device) => device.value === videoDevice?.deviceId) || videoInputDevices?.[0];
    return selectedDevice?.value;
  }, [videoInputDevices, videoDevice]);

  useCallback(() => {
    handleConfigChange('camera', getDeviceValue());
  }, [handleConfigChange, getDeviceValue]);

  useEffect(() => {
    // componentDidMount
    const acquire = async () => {
      try {
        if (!media.getPrimaryStream()) {
          await media.acquireWebcam(constraints);
        }

        if (mediaSettings.localVideoMuted) {
          await media.setVideoMute(true);
        }
      } catch (ex) {
        Logger.warn('Could not capture webcam', ex);
      }
    };
    const constraints = getConstraints(mediaSettings);

    Logger.info('attempting to capture stream', constraints);
    if (!isInCall) {
      acquire();
    }

    return function componentWillUnmount() {
      try {
        // CA-2056
        // PT: hook does not update in time
        // TODO: refactor how the start call flow goes
        if (selectors.callState() === constants.CALL_STATE_DISCONNECTED && !isJoinFlowCall) {
          // Cleanup only if out-of-call so that we don't bork existing stream
          media.stopWebcam();
          // If VBB is not enabled, this is a no-op, so no harm calling it.
          // NOTE: the mediaSettings param here is ignored, since we do not re-acquire camera (due to second param)
          vbbManager.pauseVbb(mediaSettings, false).catch((e) => Logger.warn('Error pausing VBB', e));
        }
      } catch (error) {
        Logger.warn('Error: media.stopWebcam() failed', { error });
      }
    };
    // note: an empty dependency array indicates that the effect is only run once (equivalent to componentDidMount() & [componentWillUnmount()])
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <section className={classes.container}>
      {/* <Typography component="h6" className={classes.heading}>
        {intl.formatMessage(messages.header)}
      </Typography> */}
      <section>
        <Typography component="h6" className={classes.subHeading} data-testid="cameraSelectionHeader">
          {intl.formatMessage(messages.inputCamera)}
        </Typography>
        <SettingDropdown
          handleChange={onSelectVideoSource}
          items={videoInputDevices}
          label={intl.formatMessage(messages.inputCamera)}
          testIdPrefix="camera"
          value={getDeviceValue()}
        />
      </section>
      <section>
        <Box className={classes.videoPreview}>
          <VideoStream
            elementId="vbbPreview"
            className={classes.videoMirror}
            muted={true}
            mediaStream={localPrimaryStream.stream}
          />
        </Box>
      </section>
      {vbbFeatureEnabled && (
        <section>
          <div className={classes.btnContainer}>
            <Button
              className={classes.vbbButton}
              disabled={!webGL2Supported}
              id="selectVirtualBackground"
              kind="tertiary"
              onClick={onToggleVbbSettings}
            >
              <FormattedMessage
                id="components.avSettingsModal.vbbSettingsBtn"
                defaultMessage="Select Virtual Background"
              />
              <BetaBadge type={badgeType.text} />
            </Button>
          </div>
          {!webGL2Supported && (
            <Typography variant={'h6'} className={classes.noGLWarning}>
              <WarningIcon />
              <div>
                <FormattedMessage
                  id="components.vbb.warning.webGL"
                  defaultMessage="Virtual Backgrounds require WebGL 2 which is either disabled or not available on your device"
                />
              </div>
            </Typography>
          )}
        </section>
      )}
      {!isInCall && (
        <section>
          <DeviceMute
            handleChange={() => {
              handleConfigChange('localVideoMuted', !mediaSettings.localVideoMuted);
            }}
            isInCall={isInCall}
            isJoinFlowCall={isJoinFlowCall}
            isMuted={mediaSettings.localVideoMuted}
            type={muteType.camera}
          />
        </section>
      )}
      <section>
        <Typography component="h6" className={classes.subHeading} data-testid="cameraSelectionHeader">
          {intl.formatMessage(messages.inputResolution)}
        </Typography>
        <SettingDropdown
          handleChange={(value) => handleConfigChange('resolution', value)}
          items={resolutions}
          label={intl.formatMessage(messages.inputResolution)}
          testIdPrefix="quality"
          value={mediaSettings.resolution}
        />
      </section>
    </section>
  );
};

const useStyles = makeStyles((theme: Theme) => ({
  container: {
    '& > section': {
      display: 'flex',
      flexDirection: 'column',

      '&:not(:first-child)': {
        marginTop: theme.spacing(2)
      }
    }
  },
  heading: {
    fontSize: '1.6rem'
  },
  subHeading: {
    fontSize: '1.2rem'
  },
  videoPreview: {
    flex: '0 0 auto',
    paddingTop: `calc(100% * ${defaultVideoAspect})`, // magic to maintain aspect ratio
    position: 'relative',
    width: '100%',
    '& video': {
      bottom: 0,
      height: '100%',
      left: 0,
      position: 'absolute',
      right: 0,
      top: 0,
      width: '100%'
    }
  },
  videoMirror: {
    transform: 'rotateY(180deg)'
  },
  btnContainer: {
    width: '100%', // matches video

    '& button': {
      width: 'inherit'
    }
  },
  vbbButton: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  },
  noGLWarning: {
    display: 'flex',
    fontSize: 12,
    marginTop: theme.spacing(1),
    '& svg': {
      color: colors.marigold,
      flex: '0 0 auto',
      height: 22,
      marginRight: theme.spacing(1),
      width: 'auto'
    },
    '& div': {
      // the message
      lineHeight: 1.4,
      paddingTop: '0.3em'
    }
  }
}));

export default injectIntl(VideoSettings);
